/**
 * Pure omnisearch-string utilities shared across V1 and V2 omnisearch hooks.
 *
 * Previously these four functions were copy-pasted across 8 files. See
 * apps/oak/src/hooks/omnisearch-v2/REVIEW.md (item #1) for the audit trail.
 */

/** Tokenize an omnisearch string, respecting `"…"` and `'…'` quoted groups. */
export function parseOmnisearchTokens(search: string): string[] {
  const tokens: string[] = [];
  let current = "";
  let inQuotes = false;
  let quoteChar = "";

  for (let i = 0; i < search.length; i++) {
    const char = search[i];

    if (!inQuotes && (char === '"' || char === "'")) {
      inQuotes = true;
      quoteChar = char;
      current += char;
    } else if (inQuotes && char === quoteChar) {
      inQuotes = false;
      quoteChar = "";
      current += char;
    } else if (!inQuotes && char === " ") {
      if (current.trim()) {
        tokens.push(current.trim());
        current = "";
      }
    } else {
      current += char;
    }
  }

  if (current.trim()) {
    tokens.push(current.trim());
  }

  return tokens;
}

/** Split an omnisearch string into KVP tokens (`key:value`) and non-KVP tokens. */
export function breakApartOmnisearch(search: string): {
  nonKvpValues: string;
  kvpValues: [string, string][];
} {
  const tokens = parseOmnisearchTokens(search);
  const nonKvpValues: string[] = [];
  const kvpValues: [string, string][] = [];

  for (const token of tokens) {
    const colonIndex = token.indexOf(":");
    if (colonIndex > 0) {
      const key = token.substring(0, colonIndex);
      const value = token.substring(colonIndex + 1);
      kvpValues.push([key, value]);
    } else {
      nonKvpValues.push(token);
    }
  }

  return {
    nonKvpValues: nonKvpValues.join(" "),
    kvpValues,
  };
}

/** Upsert a `key:value` pair into the omnisearch string. */
export function addOrUpdateKeyInOmnisearch(search: string, key: string, value: string): string {
  const { nonKvpValues, kvpValues } = breakApartOmnisearch(search);
  const filteredKvp = kvpValues.filter(([k]) => k !== key);
  const baseOmnisearch = `${nonKvpValues} ${filteredKvp
    .map(([k, v]) => `${k}:${v}`)
    .join(" ")}`.trim();
  const newKeyValue = `${key}:${value}`;
  return baseOmnisearch ? `${baseOmnisearch} ${newKeyValue}` : newKeyValue;
}

/** Remove every `key:value` pair matching `key` from the omnisearch string. */
export function removeKeyInOmnisearch(search: string, key: string): string {
  const { nonKvpValues, kvpValues } = breakApartOmnisearch(search);
  const filteredKvp = kvpValues.filter(([k]) => k !== key);
  return `${nonKvpValues} ${filteredKvp.map(([k, v]) => `${k}:${v}`).join(" ")}`.trim();
}
